import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { partyRoutes } from './routes/parties.js';
import { adminSystemRoutes } from './routes/admin-system.js';
import { adminOrgRoutes } from './routes/admin-org.js';
import { dataExchangeRoutes } from './routes/data-exchange.js';
import { inventoryRoutes } from './routes/inventory.js';
import { textileRoutes } from './routes/textile.js';
import { financeRoutes } from './routes/finance.js';
import { manufacturingRoutes } from './routes/manufacturing.js';
import { hrRoutes } from './routes/hr.js';
import { procurementRoutes } from './routes/procurement.js';
import { salesRoutes } from './routes/sales.js';
import { planningRoutes } from './routes/planning.js';
import { payrollRoutes } from './routes/payroll.js';
import { maintenanceRoutes } from './routes/maintenance.js';
import { assetsRoutes } from './routes/assets.js';
import { costingRoutes } from './routes/costing.js';
import { subcontractingRoutes } from './routes/subcontracting.js';
import { claimsRoutes } from './routes/claims.js';
import { crmRoutes } from './routes/crm.js';
import { helpdeskRoutes } from './routes/helpdesk.js';
import { fieldServiceRoutes } from './routes/field-service.js';
import { subscriptionRoutes } from './routes/subscriptions.js';
import { logisticsRoutes } from './routes/logistics.js';
import { wmsRoutes } from './routes/wms.js';
import { ecommerceRoutes } from './routes/ecommerce.js';
import { sustainabilityRoutes } from './routes/sustainability.js';
import { ehsRoutes } from './routes/ehs.js';
import { portalRoutes } from './routes/portal.js';
import { mesRoutes } from './routes/mes.js';
import { analyticsRoutes } from './routes/analytics.js';
import { apsRoutes } from './routes/aps.js';
import { projectsRoutes } from './routes/projects.js';
import { contractingRoutes } from './routes/contracting.js';
import { posRoutes } from './routes/pos.js';
import { qualityRoutes } from './routes/quality.js';
import { printingRoutes } from './routes/printing.js';
import { desktopRoutes } from './routes/desktop.js';
import { useSessionStore } from './stores/session.js';

declare module 'vue-router' {
  interface RouteMeta {
    /** Public routes (login) skip the auth guard entirely. */
    public?: boolean;
    /** Module id required for entitlement-gated screens (hidden from nav + redirected if missing). */
    requiresModule?: string;
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./pages/LoginPage.vue'),
    meta: { public: true },
  },
  // Printable dye batch card (M5 WP5.4): deliberately outside `AppShell` so the print CSS/@media
  // print rules never fight the sidebar/nav chrome. Still authenticated (not `public`) and still
  // module-gated, same as every other textile-dyeing screen.
  {
    path: '/textile/dye-batches/:id/card',
    name: 'textile-dye-batch-card',
    component: () => import('./pages/textile/dyeing/BatchCardPrintPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: '/',
    component: () => import('./layouts/AppShell.vue'),
    children: [
      { path: '', name: 'home', component: () => import('./pages/HomePage.vue') },
      {
        path: 'profile/security',
        name: 'profile-security',
        component: () => import('./pages/profile/SecurityPage.vue'),
      },
      {
        path: 'catalog/units',
        name: 'catalog-units',
        component: () => import('./pages/catalog/UnitsPage.vue'),
        meta: { requiresModule: 'catalog' },
      },
      {
        path: 'catalog/categories',
        name: 'catalog-categories',
        component: () => import('./pages/catalog/CategoriesPage.vue'),
        meta: { requiresModule: 'catalog' },
      },
      {
        path: 'catalog/variant-families',
        name: 'catalog-variant-families',
        component: () => import('./pages/catalog/VariantFamiliesPage.vue'),
        meta: { requiresModule: 'catalog' },
      },
      {
        path: 'catalog/items',
        name: 'catalog-items',
        component: () => import('./pages/catalog/ItemsPage.vue'),
        meta: { requiresModule: 'catalog' },
      },
      {
        path: 'catalog/items/:id',
        name: 'catalog-item-detail',
        component: () => import('./pages/catalog/ItemDetailPage.vue'),
        props: true,
        meta: { requiresModule: 'catalog' },
      },
      ...partyRoutes,
      ...adminSystemRoutes,
      ...adminOrgRoutes,
      ...dataExchangeRoutes,
      ...inventoryRoutes,
      ...textileRoutes,
      ...financeRoutes,
      ...manufacturingRoutes,
      ...hrRoutes,
      ...procurementRoutes,
      ...salesRoutes,
      ...planningRoutes,
      ...qualityRoutes,
      ...payrollRoutes,
      ...maintenanceRoutes,
      ...assetsRoutes,
      ...costingRoutes,
      ...subcontractingRoutes,
      ...claimsRoutes,
      ...crmRoutes,
      ...helpdeskRoutes,
      ...fieldServiceRoutes,
      ...subscriptionRoutes,
      ...logisticsRoutes,
      ...wmsRoutes,
      ...ecommerceRoutes,
      ...sustainabilityRoutes,
      ...ehsRoutes,
      ...portalRoutes,
      ...mesRoutes,
      ...analyticsRoutes,
      ...apsRoutes,
      ...projectsRoutes,
      ...contractingRoutes,
      ...posRoutes,
      ...printingRoutes,
      ...desktopRoutes,
    ],
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./pages/NotFoundPage.vue') },
];

export function createAppRouter() {
  const router = createRouter({ history: createWebHistory(), routes });

  router.beforeEach(async (to) => {
    if (to.meta.public) return true;

    const session = useSessionStore();
    if (session.status === 'unknown') {
      await session.refresh();
    }
    if (!session.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (to.meta.requiresModule && !session.hasModule(to.meta.requiresModule)) {
      return { name: 'home' };
    }
    return true;
  });

  return router;
}
